import React, { useState } from "react";
import {
  Dialog,
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { X } from "lucide-react";

import { createSlabTypeByMerchant } from "../../../services/slabType";
import { createSlab } from "../../../services/slabs";
import redFocusStyles from "../../../components/ui/formUI/RedFocusedStyleV2";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(6px)",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
  },
  "& .MuiPaper-root": {
    width: "90%",
    maxWidth: "100%",
    border: `1px solid ${theme.palette.mode === "dark" ? "#444" : "#d32f2f"}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.mode === "dark"
        ? "#111827"
        : theme.palette.background.paper,
    [theme.breakpoints.up("sm")]: { width: "90%" },
    [theme.breakpoints.up("md")]: { width: "600px" },
    [theme.breakpoints.up("lg")]: { width: "700px" },
  },
}));

const slabForOptions = [
  { label: "Agent", value: "agent" },
  { label: "Backoffice", value: "backoffice" },
  { label: "Customer", value: "customer" },
];

export default function CreateSlabModal({ open, onClose, refetch, slabTypes }) {
  const userInfo = useSelector((state) => state.auth.user);
  const [step, setStep] = useState(1); // 1: Select/Create Slab Type, 2: Create Slab
  const [selectedSlabType, setSelectedSlabType] = useState(null);
  const [createNewType, setCreateNewType] = useState(false);
  
  const [payload, setPayload] = useState({
 
    title: "",
    manual_generated: true,
    
  
    slabTypeId: "",
    percentage: "",
    slabFor: "agent",
  });

  const [errors, setErrors] = useState({});


  const createSlabTypeMutation = useMutation({
    mutationFn: createSlabTypeByMerchant,
    onSuccess: (response) => {
      const newSlabType = response.data;
      setSelectedSlabType(newSlabType);
      setPayload(prev => ({
        ...prev,
        slabTypeId: newSlabType.id
      }));
      setStep(2);
      toast.success("Slab type created successfully");
    },
    onError: (error) => {
      const message = error?.response?.data?.error || "Failed to create slab type";
      toast.error(message);
    },
  });


  const createSlabMutation = useMutation({
    mutationFn: createSlab,
    onSuccess: () => {
      toast.success("Slab created successfully");
      refetch();
      resetForm();
      onClose();
    },
    onError: (error) => {
      const message = error?.response?.data?.error || "Failed to create slab";
      toast.error(message);
    },
  });

  const resetForm = () => {
    setPayload({
      title: "",
      manual_generated: false,
      slabTypeId: "",
      percentage: "",
      slabFor: "agent",
    });
    setSelectedSlabType(null);
    setCreateNewType(false);
    setStep(1);
    setErrors({});
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (createNewType && !payload.title) {
      newErrors.title = "Slab type title is required";
    }
    if (!createNewType && !selectedSlabType) {
      newErrors.slabType = "Please select a slab type";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!payload.percentage) {
      newErrors.percentage = "Percentage is required";
    } else if (isNaN(payload.percentage) || payload.percentage < 0 || payload.percentage > 100) {
      newErrors.percentage = "Percentage must be between 0 and 100";
    }
    if (!payload.slabFor) {
      newErrors.slabFor = "Slab for is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePayloadChange = (field, value) => {
    setPayload((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleNextStep = async () => {
    if (step === 1) {
      if (!validateStep1()) return;

      if (createNewType) {
     
        await createSlabTypeMutation.mutateAsync({
          title: payload.title,
          manual_generated: payload.manual_generated
        });
      } else {

        setPayload(prev => ({
          ...prev,
          slabTypeId: selectedSlabType.id
        }));
        setStep(2);
      }
    }
  };

  const handleCreateSlab = async () => {
    if (!validateStep2()) return;

    await createSlabMutation.mutateAsync({
      slabTypeId: payload.slabTypeId,
      percentage: parseFloat(payload.percentage),
      slabFor: payload.slabFor,
      isCountry: false,
      countryId: null,
      isProvider: false,
      providerId: null,
      userId: userInfo.id
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const loading = createSlabTypeMutation.isPending || createSlabMutation.isPending;

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <div className="px-5 py-3">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            {step === 1 ? "Select Slab Type" : "Create Slab"}
          </h3>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

  
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 1 ? "bg-[#CD0C02] text-white" : "bg-gray-300 text-gray-600"
            }`}>
              1
            </div>
            <div className={`w-20 h-1 ${step >= 2 ? "bg-[#CD0C02]" : "bg-gray-300"}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 2 ? "bg-[#CD0C02] text-white" : "bg-gray-300 text-gray-600"
            }`}>
              2
            </div>
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="flex gap-4 mb-4">
              <button
                onClick={() => setCreateNewType(false)}
                className={`flex-1 py-2 px-4 rounded-md border ${
                  !createNewType 
                    ? "bg-[#CD0C02] text-white border-[#CD0C02]" 
                    : "bg-white text-gray-700 border-gray-300"
                }`}
              >
                Select Existing Type
              </button>
              <button
                onClick={() => setCreateNewType(true)}
                className={`flex-1 py-2 px-4 rounded-md border ${
                  createNewType 
                    ? "bg-[#CD0C02] text-white border-[#CD0C02]" 
                    : "bg-white text-gray-700 border-gray-300"
                }`}
              >
                Create New Type
              </button>
            </div>

            {!createNewType ? (
              <Autocomplete
                options={slabTypes}
                getOptionLabel={(option) => option.title}
                value={selectedSlabType}
                onChange={(e, newValue) => setSelectedSlabType(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Slab Type"
                    sx={redFocusStyles}
                    error={!!errors.slabType}
                    helperText={errors.slabType}
                    required
                  />
                )}
              />
            ) : (
              <div className="space-y-4">
                <TextField
                  fullWidth
                  label="Slab Type Title"
                  value={payload.title}
                  onChange={(e) => handlePayloadChange("title", e.target.value)}
                  sx={redFocusStyles}
                  error={!!errors.title}
                  helperText={errors.title}
                  required
                />
                {/* <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="manual_generated"
                    checked={payload.manual_generated}
                    onChange={(e) => handlePayloadChange("manual_generated", e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="manual_generated" className="text-sm">
                    Manual Generated
                  </label>
                </div> */}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-md">
              <Typography variant="body2" color="textSecondary">
                Selected Slab Type: <strong>{selectedSlabType?.title}</strong>
              </Typography>
            </div>

            <TextField
              fullWidth
              label="Percentage"
              type="number"
              value={payload.percentage}
              onChange={(e) => handlePayloadChange("percentage", e.target.value)}
              sx={redFocusStyles}
              error={!!errors.percentage}
              helperText={errors.percentage}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              required
            />

            {/* <Autocomplete
              options={slabForOptions}
              getOptionLabel={(option) => option.label}
              value={slabForOptions.find(opt => opt.value === payload.slabFor) || slabForOptions[0]}
              onChange={(e, newValue) => handlePayloadChange("slabFor", newValue?.value || "agent")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Slab For"
                  sx={redFocusStyles}
                  error={!!errors.slabFor}
                  helperText={errors.slabFor}
                  required
                />
              )}
            /> */}
          </div>
        )}

        <div className="w-full flex justify-end mt-6 gap-3">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className="py-2 px-6 border border-gray-400 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Back
            </button>
          )}
          <button
            onClick={handleClose}
            className="py-2 px-6 border border-gray-400 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            Close
          </button>
          {step === 1 ? (
            <button
              onClick={handleNextStep}
              className="text-white bg-[#CD0C02] py-2 px-6 rounded-md hover:bg-red-700 disabled:bg-gray-400"
              disabled={loading}
            >
              {createSlabTypeMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Next"
              )}
            </button>
          ) : (
            <button
              onClick={handleCreateSlab}
              className="text-white bg-[#CD0C02] py-2 px-6 rounded-md hover:bg-red-700 disabled:bg-gray-400"
              disabled={loading}
            >
              {createSlabMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Create Slab"
              )}
            </button>
          )}
        </div>
      </div>
    </StyledDialog>
  );
}